import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Row, Col, Card, Collapse, Form, Container, Button } from 'react-bootstrap';
import { SingleSelectAsync, SelectOption } from './SIngleSelectAsync';
import HeaderComponent from './HeaderComponent';
import FeatureNav from './FeatureNav';
import BreadCrumbs from './BreadCrumbs';
import Loader from './Loader';
import { Tabulator, PageModule, DownloadModule, ExportModule, SortModule } from "tabulator-tables";
import { prodApiEndpointUrl, stagingApiEndpointUrl } from 'types/apiEndpoints';
import "tabulator-tables/dist/css/tabulator.min.css";
import FooterSection from './FooterSection';

Tabulator.registerModule([PageModule, DownloadModule, ExportModule, SortModule]);

interface AllEntity {
  entity_name: string;
  entity_id: number;
}

interface EntityCompensation {
  entity_id: number;
  entity_name: string;
  fiscal_year: number;
  fund1: string;
  fund2: string;
  fund3: string;
  fund4: string;
  org1: string;
  org2: string;
  org3: string;
  org4: string;
  cat1: string;
  cat2: string;
  cat3: string;
  cat4: string;
  description: string;
  employee_name: string;
  title: string;
  net_amount: number;
}

const EntityCompensationDownloader = () => {
  const [showHeaderInfo, setShowHeaderInfo] = useState<boolean>(false);
  const [selectedEntityName, setSelectedEntityName] = useState<string | null>(null);
  const [selectedFiscalYear, setSelectedFiscalYear] = useState<number | null>(null);
  const [allEntities, setAllEntities] = useState<AllEntity[]>([]);
  const [allFiscalYears, setAllFiscalYears] = useState<string[] | null>(null);
  const [masterData, setMasterData] = useState<EntityCompensation[]>([]);
  const tabulatorRef = useRef<InstanceType<typeof Tabulator> | null>(null);
  const tableRef = useRef<HTMLDivElement>(null);
  const [apiEndpoint] = useState<string>(() => {
    return window.location.hostname === "transparent.utah.gov" ? prodApiEndpointUrl : stagingApiEndpointUrl;
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [entitiesLoading, setEntitiesLoading] = useState<boolean>(false);
  const entityOptionsRef = useRef<SelectOption[]>([]);

  const handleDownload = (type: "csv" | "xlsx" | "html" | "json") => {
    if (tabulatorRef.current) {
      const formattedEntityName = selectedEntityName?.toLowerCase()?.replaceAll(" ", "_") || "entity";

      tabulatorRef.current.download(
        type, 
        `${formattedEntityName}_compensation_${selectedFiscalYear}.${type}`
      );
    }
  }

  const memoizedEntityNameOptions = useMemo(() => {
    if (!allEntities) return [];
    const filteredEntities = allEntities.filter(entity => entity.entity_name !== "-- Select --");
    const rawNames = filteredEntities.map(entity => String(entity.entity_name || entity));
    const uniqueNames = [...new Set(rawNames)];
    uniqueNames.sort((a, b) => a.localeCompare(b));
    
    const formattedOptions = uniqueNames.map(name => ({ value: name, label: name }));
    
    entityOptionsRef.current = formattedOptions;
    
    return formattedOptions;
  }, [allEntities]);

  const loadEntityOptions = useCallback(async (inputValue: string): Promise<SelectOption[]> => {
    if (!inputValue) {
      return memoizedEntityNameOptions;
    }
    const search = inputValue.toLowerCase();
    return memoizedEntityNameOptions.filter(option => 
      option.label.toLowerCase().includes(search)
    );
  }, [memoizedEntityNameOptions]); 

  const memoizedFiscalYearOptions = useMemo(() => {
    if (!allFiscalYears) return [];

    return [...allFiscalYears]
      .sort((a, b) => b.localeCompare(a, undefined, { numeric: true }))
      .map(year => ({
        value: String(year),
        label: String(year)
      }));
  }, [allFiscalYears]);
  
  const loadFiscalYearOptions = async (inputValue: string): Promise<SelectOption[]> => {
    if (inputValue) {
      return memoizedFiscalYearOptions.filter(option =>
        option.label.toLowerCase().includes(inputValue.toLowerCase())
      );
    }
    return memoizedFiscalYearOptions;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    await fetchEntityCompensationData();
  };

  const buildApiUrl = (apiEndpointUrl: string, searchParameter: string) => {
    return `${apiEndpointUrl}${searchParameter}`;
  };

  const getAllEntitiesAndFiscalYears = async(): Promise<void> => {
    setEntitiesLoading(true);

    try {
      const searchString = `?function=getAvailableEntitiesByYearPY&parameter=%7B%7D`;
      const res = await fetch(buildApiUrl(apiEndpoint, searchString));
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
      const data = res.ok ? await res.json(): [];

      const entityNamesAndIds = data.filter((entity: { entity_name: string; entity_id: number }) => 
        entity.entity_name && 
        entity.entity_name !== "-- Select --" &&
        entity.entity_id 
      );

      const getFiscalYearFormOptions = (data: any[], key: string) => {
        const unique = [...new Set(data.map(item => item[key]?.toString()))]
          .filter(Boolean)
          .sort((a, b) => a.localeCompare(b));

        return unique.map(val => ({
          value: val,
          label: val
        }));
      }

      const fiscalYears = getFiscalYearFormOptions(data, "fiscal_year");
      const fiscalYearArray = fiscalYears
        .map(fiscalYear => String(fiscalYear.value))
        .sort((a, b) => b.localeCompare(a, undefined, { numeric: true }));

      setAllEntities(entityNamesAndIds);
      setAllFiscalYears(fiscalYearArray);

    } catch (error: any) {
    } finally {
      setEntitiesLoading(false);
    }
  };

  const fetchEntityCompensationData = async () => {
    if (!selectedEntityName || !selectedFiscalYear) {
      setLoading(false);
      return;
    }

    const selectedMatchedEntity = allEntities.find(entity => entity.entity_name === selectedEntityName);
    const entityId = selectedMatchedEntity ? selectedMatchedEntity.entity_id.toString() : null;

    const largeEntityIds: number[] = [259, 45, 207, 57, 35, 73, 17, 331, 1, 3, 5, 263];

    if (!entityId) {
      console.error("Error - could not find ID for selected entity");
      return;
    }

    setLoading(true);

    try {
      const payload = {
        entity_name: entityId,
        fiscal_year: selectedFiscalYear.toString(),
      }

      if (entityId && largeEntityIds.includes(Number(entityId))) {
        const largeEntityUrl = `https://storage.googleapis.com/tu-large-entity-data-response-bucket/entity_compensation/${entityId}/${selectedFiscalYear}.json`;
        const res = await fetch(largeEntityUrl);
        const entityCompensationData = res.ok ? await res.json() : [];

        setMasterData(entityCompensationData);
      } else {
        const encodedParam = encodeURIComponent(JSON.stringify(payload));
        const entityCompensationSearchString = `?function=getEntityCompensation&parameter=${encodedParam}`;

        const res = await fetch(buildApiUrl(apiEndpoint, entityCompensationSearchString));
        const entityCompensationData = res.ok ? await res.json() : [];

        setMasterData(entityCompensationData);
      }
      
    } catch (error: any) {
    } finally {
      setLoading(false);
    }
  }
  
  // Get All Entities and Fiscal Years
  useEffect(() => {
    getAllEntitiesAndFiscalYears();
  }, [])

  // Initialize Tabulator Table
  useEffect(() => {
    setLoading(true);
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) && window.innerWidth < 1025;

    const table = tabulatorRef.current = new Tabulator(tableRef.current, {
      data: masterData,
      minHeight: "450px",
      maxHeight: "100%",
      layout: isMobile ? "fitDataFill" : "fitColumns",
      pagination: "local",
      paginationSize: 10,
      placeholder: "No Data Available",
      headerSort: true,
      initialSort: [
        {
          column: "employee_name",
          dir: "asc",
        }
      ],
      columns: [
        {
          titleFormatter: () => {
            const el = document.createElement("span");
            el.innerHTML = "<i class='fas fa-landmark'></i> Entity";
            return el;
          },
          title: "Entity",
          field: "entity_name",
        },
        {
          titleFormatter: () => {
            const el = document.createElement("span");
            el.innerHTML = "<i class='fas fa-calendar'></i> Year";
            return el;
          },
          title: "Fiscal Year",
          field: "fiscal_year",
        },
        {
          titleFormatter: () => {
            const el = document.createElement("span");
            el.innerHTML = "<i class='fas fa-user'></i> Employee";
            return el;
          },
          title: "Employee",
          field: "employee_name",
        },
        {
          titleFormatter: () => {
            const el = document.createElement("span");
            el.innerHTML = "<i class='fas fa-user'></i> Title";
            return el;
          },
          title: "Title",
          field: "title",
        },
        {
          titleFormatter: () => {
            const el = document.createElement("span");
            el.innerHTML = "<i class='fas fa-dollar-sign'></i> Net Amount";
            return el;
          },
          title: "Net Amount",
          field: "net_amount",
          formatter: "money",
          formatterParams: { symbol: "$" }
        },
        {
          titleFormatter: () => {
            const el = document.createElement("span");
            el.innerHTML = "<i class='fas fa-user'></i> Description";
            return el;
          },
          title: "Description",
          field: "description",
        },
        ...(masterData.some(row => row.fund1 !== null) ? [{
          title: "Fund1",
          field: "fund1",
          titleFormatter: () => {
            const el = document.createElement("span");
            el.innerHTML = "<i class='fas fa-sack-dollar'></i> Fund1";
            return el;
          },
        }] : []),
        ...(masterData.some(row => row.fund2 !== null) ? [{
          title: "Fund2",
          field: "fund2",
          titleFormatter: () => {
            const el = document.createElement("span");
            el.innerHTML = "<i class='class='fas fa-sack-dollar'></i> Fund4";
            return el;
          },
        }] : []),
        ...(masterData.some(row => row.fund3 !== null) ? [{
          title: "Fund3",
          field: "fund3",
          titleFormatter: () => {
            const el = document.createElement("span");
            el.innerHTML = "<i class='class='fas fa-sack-dollar''></i> Fund3";
            return el;
          },
        }] : []),
        ...(masterData.some(row => row.fund4 !== null) ? [{
          title: "Fund4",
          field: "fund4",
          titleFormatter: () => {
            const el = document.createElement("span");
            el.innerHTML = "<i class='class='fas fa-sack-dollar''></i> Fund4";
            return el;
          },
        }] : []),
        ...(masterData.some(row => row.org1 !== null) ? [{
          title: "Org1",
          field: "org1",
          titleFormatter: () => {
            const el = document.createElement("span");
            el.innerHTML = "<i class='fas fa-network-wired'></i> Org1";
            return el;
          },
        }] : []),
        ...(masterData.some(row => row.org2 !== null) ? [{
          title: "Org2",
          field: "org2",
          titleFormatter: () => {
            const el = document.createElement("span");
            el.innerHTML = "<i class='fas fa-network-wired'></i> Org2";
            return el;
          },
        }] : []),
        ...(masterData.some(row => row.org3 !== null) ? [{
          title: "Org3",
          field: "org3",
          titleFormatter: () => {
            const el = document.createElement("span");
            el.innerHTML = "<i class='fas fa-network-wired'></i> Org3";
            return el;
          },
        }] : []),
        ...(masterData.some(row => row.org4 !== null) ? [{
          title: "Org4",
          field: "org4",
          titleFormatter: () => {
            const el = document.createElement("span");
            el.innerHTML = "<i class='fas fa-network-wired'></i> Org4";
            return el;
          },
        }] : []),
        ...(masterData.some(row => row.cat1 !== null) ? [{
          title: "Cat1",
          field: "cat1",
          titleFormatter: () => {
            const el = document.createElement("span");
            el.innerHTML = "<i class='fas fa-boxes-stacked'></i> Cat1";
            return el;
          },
        }] : []),
        ...(masterData.some(row => row.cat2 !== null) ? [{
          title: "Cat2",
          field: "cat2",
          titleFormatter: () => {
            const el = document.createElement("span");
            el.innerHTML = "<i class='fas fa-boxes-stacked'></i> Cat2";
            return el;
          },
        }] : []),
        ...(masterData.some(row => row.cat3 !== null) ? [{
          title: "Cat3",
          field: "cat3",
          titleFormatter: () => {
            const el = document.createElement("span");
            el.innerHTML = "<i class='fas fa-boxes-stacked'></i> Cat3";
            return el;
          },
        }] : []),
        ...(masterData.some(row => row.cat4 !== null) ? [{
          title: "Cat4",
          field: "cat4",
          titleFormatter: () => {
            const el = document.createElement("span");
            el.innerHTML = "<i class='fas fa-boxes-stacked'></i> Cat4";
            return el;
          },
        }] : []),
      ]
    })

    tabulatorRef.current = table;
    setLoading(false);

    return () => {
      table.destroy();

      if (tabulatorRef.current === table) {
        tabulatorRef.current = null;
      }
    };
  }, [masterData]);

  return (
    <>
      <HeaderComponent/>
      <FeatureNav/>
      <BreadCrumbs/>
      <Container fluid as="main">
        {/* Header */}
        <Row className="mb-4 px-4">
          <Col xs={12} className="px-0">
            <Card bg="secondary" text="white" style={{ border: "none", borderRadius: "0" }}>
              <Card.Body style={{ justifyItems: "center", textAlign: "center" }}>
                <div className="tu_tabHeader">
                  <div className="tu-tabHeader__titleRow">
                    <h1 className="tu-tabHeader__title">
                    Entity Compensation Downloader</h1>
                      <button
                      type="button"
                      className="tu-tabHeader__infoBtn"
                      onClick={() => setShowHeaderInfo(!showHeaderInfo)}
                      aria-label="Entity Compensation Downloader Info"
                    >
                      <i className="fas fa-info"/>
                      </button>
                  </div>
                </div>

                <Collapse in={showHeaderInfo}>
                  <div>
                    <div>
                      <p className="lead mt-2 text-center">
                        This page allows you to download ALL employee compensation for a selected entity and given year. Once you click submit you will then get a preview of the data in the table below.
                      </p>
                      <p className="lead mt-2 text-center">
                        If you select an entity with many employees then your search may take up to 1 minute to complete. The data will be downloaded as a zipped csv file to your machine, so there will not be a preview of the data in the web browser. A list of these large entities is below:
                      </p>
                    </div>
                    <Row>
                      <Col xs={12} sm={12} md={4} lg={4} xl={4}>
                        <ul>
                          <li>Alpine School District</li>
                          <li>Canyons School District</li>
                          <li>Davis School District</li>
                          <li>Granite School District</li>
                        </ul>
                      </Col>
                      <Col xs={12} sm={12} md={4} lg={4} xl={4}>
                        <ul>
                          <li>Jordan School District</li>
                          <li>Nebo School District</li>
                          <li>Salt Lake Community College</li>
                          <li>Salt Lake County</li>
                        </ul>
                      </Col>
                      <Col xs={12} sm={12} md={4} lg={4} xl={4}>
                        <ul>
                          <li>State of Utah</li>
                          <li>University of Utah</li>
                          <li>Utah State University</li>
                          <li>Washington County School District</li>
                        </ul>
                      </Col>
                    </Row>
                  </div>
                </Collapse>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Loader */}
        {entitiesLoading ? <Loader /> : <></>}

        {/* Search Form */}
        <Form onSubmit={handleSubmit}>
          <Row className="px-4 mb-3">
            <Card className="px-0">
              <Card.Header className="bg-secondary text-white" style={{ border: "none"}}>
                <h3 className="text-white">Search</h3>
              </Card.Header>
              <Card.Body className="p-2">
                <Row>
                  <Col xs={12} sm={12} md={4} lg={4} xl={4} className="my-3">
                    <h5>
                      <i className="fas fa-landmark"/>
                      {" "}Select an Entity
                    </h5>
                      <SingleSelectAsync
                        value={selectedEntityName ? { label: selectedEntityName, value: selectedEntityName } : null}
                        onChange={(newValue) => setSelectedEntityName(newValue ? newValue.value : null)}
                        loadOptions={loadEntityOptions}
                        defaultOptions={memoizedEntityNameOptions.length > 0 ? memoizedEntityNameOptions : true} 
                        type="entity"
                      />
                  </Col>

                  <Col xs={12} sm={12} md={4} lg={4} xl={4} className="my-3">
                    <h5>
                      <i className="fas fa-landmark"/>
                      {" "}Select a Fiscal Year
                    </h5>
                      <SingleSelectAsync
                        value={selectedFiscalYear ? { label: String(selectedFiscalYear), value: String(selectedFiscalYear) } : null}
                        key={memoizedFiscalYearOptions.length}
                        onChange={(newValue) => {
                          const val = newValue ? Number(newValue.value) : null;
                          setSelectedFiscalYear(val);
                        }}
                        loadOptions={loadFiscalYearOptions}
                        type="title"
                        defaultOptions={memoizedFiscalYearOptions}
                      />
                  </Col>

                  <Col xs={12} sm={12} md={4} lg={4} xl={4} className="my-3">
                    <button
                      type="submit"
                      className="btn btn-secondary"
                      aria-label="Submit"
                      style={{ marginTop: "33px"}}
                    >
                      <i className="fas fa-paper-plane"></i>{" "} Submit
                    </button>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Row>
        </Form>

        {/* Data Section */}
        <Row className="px-4 mb-3">
          <Col xs={12} className="px-0">
            <Card>
              <Card.Header className="bg-secondary text-white" style={{ border: "none"}}>
                <h3 className="text-white">Data</h3>
              </Card.Header>
              <Card.Body>
                <div className="mb-2 d-flex gap-2">
                  <button className="btn btn-secondary" onClick={() => handleDownload("csv")}>CSV</button>
                  <button className="btn btn-secondary" onClick={() => handleDownload("xlsx")}>Excel</button>
                  <button className="btn btn-secondary" onClick={() => handleDownload("html")}>HTML</button>
                  <button className="btn btn-secondary" onClick={() => handleDownload("json")}>JSON</button>
                </div>
                <div style={{ width: "100%", position: "relative"}}>
                  {/* Loading */}
                  {loading && (
                    <div className="text-center py-5">
                      <div className="text-primary" role="status"></div>
                      {/* Loader */}
                      {loading ? <Card.Text><Loader /></Card.Text> : <></>}
                    </div>
                  )}

                  {/* No Data Available */}
                  {!loading && (!masterData || masterData.length === 0) && (
                    <div className="d-flex justify-content-center align-items-center py-5">
                      <Card.Text className="text-center m-0 px-4">
                        No data available for the selected filters. Try adjusting your search or selecting a new search term.
                      </Card.Text>
                    </div>
                  )}

                  <div 
                    ref={tableRef} 
                    className="table-light"
                    style={{ 
                      minHeight: 400,
                      visibility: (!loading && masterData &&
                         masterData.length > 0) ? 'visible' : 'hidden',
                      height: 'auto',
                      overflow: 'hidden'
                    }} 
                  />
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
      <FooterSection/>
    </>
  )
}

export default EntityCompensationDownloader;